import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import Toggle from '@/components/Toggle';
import BasicInput from '@/components/BasicInput';
import ImageUpload from '@/components/ImageUpload';
import Select from '@/components/Select';
import cardFrontIcon from '@/assets/card_front.svg';
import cardBackIcon from '@/assets/card_back.svg';
import cardLtIcon from '@/assets/card_lt.svg';
import cardRtIcon from '@/assets/card_rt.svg';
import cardLbIcon from '@/assets/card_lb.svg';
import cardRbIcon from '@/assets/card_rb.svg';
import cardStainIcon from '@/assets/card_stain.svg';
import addIcon from '@/assets/card_more.svg';

const PSACardInfo = ({
  grade,
  onGradeChange,
  price,
  onPriceChange,
  priceInputRef,
  isPriceAdjustMode,
  images,
  onImageUpload,
  onImageDelete,
  onImagePreview,
  cardStats
}) => {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState(null);

  // Additional focus mechanism
  useEffect(() => {
    if (isPriceAdjustMode && priceInputRef?.current) {
      priceInputRef.current.focus();
    }
  }, [priceInputRef, isPriceAdjustMode]);

  const psaGrades = [
    { value: 'PSA_10', label: t('psa.grades.psa_10'), subLabel: '-' },
    { value: 'PSA_9', label: t('psa.grades.psa_9'), subLabel: '-' },
    { value: 'PSA_8', label: t('psa.grades.psa_8'), subLabel: '-' },
    { value: 'PSA_7', label: t('psa.grades.psa_7'), subLabel: '-' },
    { value: 'PSA_6', label: t('psa.grades.psa_6'), subLabel: '-' }
  ].map(gradeItem => {
    if (cardStats?.psaStats) {
      const statForGrade = cardStats.psaStats.find(stat => stat.psa === gradeItem.value);
      if (statForGrade && statForGrade.lowestPriceOnSale) {
        return {
          ...gradeItem,
          subLabel: `¥ ${statForGrade.lowestPriceOnSale.toLocaleString()}`
        };
      }
    }
    return gradeItem;
  });

  // PSA cards have front, back, and extra images (all optional)
  const psaImageLabels = ['psa_front', 'psa_back'];
  const psaImagePlaceholders = [cardFrontIcon, cardBackIcon];

  // Prepare display data for PSA image slots (front and back)
  const psaImageSlots = psaImageLabels.map((label, index) => {
    return {
      label,
      index,
      url: images[index]?.url || null,
      placeholder: psaImagePlaceholders[index]
    };
  });

  // Count of actual extra images uploaded (starting from index 2 for PSA)
  const extraImagesCount = images.slice(2).filter(img => img !== null && img !== undefined).length;
  
  // Determine if we should show an extra upload slot (max 9 extra images)
  const showExtraUploadSlot = extraImagesCount < 9;

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full justify-center items-center inline-flex">
        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1.5 inline-flex">
          <div className="text-[#666666] text-sm font-normal">
            {t('sell.product_info.psa_grade')}
          </div>
          <Toggle
            items={psaGrades}
            value={grade}
            onChange={onGradeChange}
            className="w-full h-16 bg-white rounded-md border border-[#dddddd]"
          />
        </div>
      </div>

      <BasicInput
        type="text"
        title={t('sell.product_info.price')}
        value={price || ''}
        placeholder={price && '0'}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9]/g, '');
          onPriceChange({ target: { value: value ? parseInt(value) : '' } });
        }}
        prefix="¥"
        ref={priceInputRef}
      />

      <div className="flex flex-col gap-1.5">
        <div className="text-[#666666] text-sm">
          {t('sell.product_info.upload_images')}
        </div>
        
        <div className="text-[#d48b00] text-xs">
          {t('sell.product_info.upload_reminder')}
        </div>
        
        {uploadError && (
          <div className="p-3 mb-2 bg-red-50 text-red-700 rounded-md border border-red-300">
            {uploadError}
          </div>
        )}
        
        <div className="grid grid-cols-4 gap-3">
          {/* PSA image slots - front and back */}
          {psaImageSlots.map(slot => (
            <ImageUpload
              key={slot.label}
              label={t(`sell.product_info.${slot.label}`)}
              imageUrl={slot.url}
              placeholderImage={slot.placeholder}
              onUpload={() => onImageUpload(slot.index, setUploadError)}
              onDelete={() => onImageDelete(slot.index)}
              onPreview={() => onImagePreview(slot.index)}
            />
          ))}

          {/* Show extra slots progressively */}
          {Array.from({ length: Math.min(extraImagesCount + 1, 9) }).map((_, i) => {
            const slotIndex = 2 + i;
            const hasImage = images[slotIndex] && images[slotIndex] !== null;
            return (
              <ImageUpload
                key={`psa-extra-${i}`}
                label={hasImage ? t('sell.product_info.extra_image', { number: i + 1 }) : t('sell.product_info.upload_more')}
                imageUrl={hasImage ? images[slotIndex].url : null}
                placeholderImage={addIcon}
                onUpload={() => onImageUpload(slotIndex, setUploadError)}
                onDelete={hasImage ? () => onImageDelete(slotIndex) : undefined}
                onPreview={hasImage ? () => onImagePreview(slotIndex) : undefined}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const NonPSACardInfo = ({
  condition,
  onConditionChange,
  description,
  onDescriptionChange,
  price,
  onPriceChange,
  priceInputRef,
  isPriceAdjustMode,
  images,
  onImageUpload,
  onImageDelete,
  onImagePreview,
  cardStats
}) => {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState(null);

  // Additional focus mechanism
  useEffect(() => {
    if (priceInputRef?.current && isPriceAdjustMode) {
      priceInputRef.current.focus();
    }
  }, [priceInputRef, isPriceAdjustMode]);

  const conditions = [
    { value: 'MINT', label: t('sell.product_info.conditions.mint'), subLabel: '-' },
    { value: 'NEAR_MINT', label: t('sell.product_info.conditions.near_mint'), subLabel: '-' },
    { value: 'FAIR', label: t('sell.product_info.conditions.fair'), subLabel: '-' },
    { value: 'POOR', label: t('sell.product_info.conditions.poor'), subLabel: '-' }
  ].map(conditionItem => {
    if (cardStats?.conditionStats) {
      const statForCondition = cardStats.conditionStats.find(
        stat => stat.condition === conditionItem.value
      );
      if (statForCondition && statForCondition.lowestPriceOnSale) {
        return {
          ...conditionItem,
          subLabel: `¥ ${statForCondition.lowestPriceOnSale.toLocaleString()}`
        };
      }
    }
    return conditionItem;
  });

  // Required image slots with their labels and placeholder icons
  const requiredImageLabels = [
    'front',
    'back',
    'front_top_left',
    'front_top_right',
    'front_bottom_left',
    'front_bottom_right',
    'damage',
  ];

  const requiredImagePlaceholders = [
    cardFrontIcon,
    cardBackIcon,
    cardLtIcon,
    cardRtIcon,
    cardLbIcon,
    cardRbIcon,
    cardStainIcon,
  ];

  // Prepare display data for required slots
  const requiredImageSlots = requiredImageLabels.map((label, index) => {
    // Find this image in the images array if it exists
    // Images might not be in order, or some slots might be empty
    return {
      label,
      index,
      url: images[index]?.url || null,
      placeholder: requiredImagePlaceholders[index]
    };
  });
  
  // Count of extra images (after the 7 required ones)
  const extraImagesCount = Math.max(0, images.length - 7);
  
  // Determine if we should show an extra upload slot (max 9 extra images)
  const showExtraUploadSlot = extraImagesCount < 9;

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full justify-center items-center inline-flex">
        <div className="grow shrink basis-0 self-stretch flex-col justify-start items-start gap-1.5 inline-flex">
          <div className="text-[#666666] text-sm font-normal">
            {t('sell.product_info.condition')}
          </div>
          <Toggle
            items={conditions}
            value={condition}
            onChange={onConditionChange}
            className="w-full h-16 bg-white rounded-md border border-[#dddddd]"
          />
        </div>
      </div>

      <div className="flex gap-4">
        <BasicInput
          title={t('sell.product_info.price')}
          type="text"
          value={price || ''}
          placeholder={price && '0'}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            onPriceChange({ target: { value: value ? parseInt(value) : '' } });
          }}
          containerClassName="w-full"
          prefix="¥"
          ref={priceInputRef}
        />
      </div>

      <div className="w-full h-[138px] flex-col justify-center items-start gap-1 flex">
        <div className="text-[#111111] text-sm font-normal leading-[21px]">
          {t('sell.product_info.description')}
        </div>
        <textarea
          value={description}
          onChange={onDescriptionChange}
          placeholder={t('sell.product_info.description_placeholder')}
          maxLength={1000}
          className="w-full h-[113px] p-3 bg-[#f7f7f7] rounded-md border border-[#dddddd] resize-none placeholder:text-[#aaaaaa] text-[#111111]"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="text-[#666666] text-sm">
          {t('sell.product_info.upload_images')}
        </div>
        
        <div className="text-[#d48b00] text-xs">
          {t('sell.product_info.upload_reminder')}
        </div>
        
        {uploadError && (
          <div className="p-3 mb-2 bg-red-50 text-red-700 rounded-md border border-red-300">
            {uploadError}
          </div>
        )}
        
        <div className="grid grid-cols-4 gap-3">
          {/* Required image slots */}
          {requiredImageSlots.map(slot => (
            <ImageUpload
              key={slot.label}
              label={t(`sell.product_info.${slot.label}`)}
              imageUrl={slot.url}
              placeholderImage={slot.placeholder}
              onUpload={() => onImageUpload(slot.index, setUploadError)}
              onDelete={() => onImageDelete(slot.index)}
              onPreview={() => onImagePreview(slot.index)}
            />
          ))}
          
          {/* Extra images (if any) */}
          {extraImagesCount > 0 && Array.from({ length: extraImagesCount }).map((_, i) => {
            const imageIndex = 7 + i;
            return (
              <ImageUpload
                key={`extra-${i}`}
                label={t('sell.product_info.extra_image', { number: i + 1 })}
                imageUrl={images[imageIndex]?.url}
                placeholderImage={addIcon}
                onUpload={() => onImageUpload(imageIndex, setUploadError)}
                onDelete={() => onImageDelete(imageIndex)}
                onPreview={() => onImagePreview(imageIndex)}
              />
            );
          })}
          
          {/* Extra upload slot (if allowed) */}
          {showExtraUploadSlot && (
            <ImageUpload
              key="upload-more"
              label={t('sell.product_info.upload_more')}
              imageUrl={null}
              placeholderImage={addIcon}
              onUpload={() => onImageUpload(7 + extraImagesCount, setUploadError)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const SealedBoxInfo = ({
  price,
  onPriceChange,
  quantity,
  onQuantityChange,
  priceInputRef,
  isPriceAdjustMode,
  cardStats
}) => {
  const { t } = useTranslation();

  // Additional focus mechanism
  useEffect(() => {
    if (priceInputRef?.current && isPriceAdjustMode) {
      priceInputRef.current.focus();
    }
  }, [priceInputRef, isPriceAdjustMode]);

  // Generate quantity options from cardStats bundledQuantityStats
  const quantityOptions = (() => {
    if (cardStats?.bundledQuantityStats) {
      return cardStats.bundledQuantityStats.map(stat => ({
        value: String(stat.quantity),
        label: String(stat.quantity)
      }));
    }
    
    // Fallback to predefined quantity options if no stats available
    return [1, 2, 3, 4, 5, 10, 15, 20, 30, 40, 50].map(quantity => ({
      value: String(quantity),
      label: String(quantity)
    }));
  })();

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full justify-start items-start gap-4 inline-flex">
        <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
          <div className="text-[#111111] text-sm font-normal leading-[21px]">
            {t('sell.product_info.quantity')}
          </div>
          <Select
            options={quantityOptions}
            value={String(quantity)}
            onValueChange={(value) => onQuantityChange({ target: { value: Number(value) } })}
            className="w-full h-12 bg-[#f7f7f7] text-left rounded-md border border-[#dddddd]"
            maxHeight="300px"
          />
        </div>
        
        <BasicInput
            type="text"
            title={t('sell.product_info.price')}
            value={price || ''}
            placeholder={price && '0'}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              onPriceChange({ target: { value: value ? parseInt(value) : '' } });
            }}
            prefix="¥"
            containerClassName="grow shrink basis-0"
            ref={priceInputRef}
        />
      </div>
    </div>
  );
};

const CardSellInfoSection = ({
  type,
  cardStats,
  onTypeChange,
  isEditMode,
  priceInputRef,
  isPriceAdjustMode,
  ...props
}) => {
  const { t } = useTranslation();

  const handleCardTypeClick = () => {
    if (onTypeChange && !isEditMode) {
      // Toggle between PSA and non-PSA single card
      const newType = type === 'PSA_SINGLE_CARD' ? 'NON_PSA_SINGLE_CARD' : 'PSA_SINGLE_CARD';
      onTypeChange(newType);
    }
  };

  return (
    <div className="bg-white rounded-md">
      <div className="w-full py-6 px-6 justify-start items-center inline-flex">
        <div className="justify-start items-center gap-4 flex flex-1">
          <h2 className="text-[#111111] text-lg font-bold leading-[27px] flex-1">
            {t('sell.product_info.title')}
          </h2>
          {!isEditMode && type === 'PSA_SINGLE_CARD' && (
            <div 
              className="text-[#2966ff] text-sm font-normal leading-[21px] cursor-pointer hover:underline"
              onClick={handleCardTypeClick}
            >
              {t('sell.product_info.non_psa_card_indicator')}
            </div>
          )}
          {!isEditMode && type === 'NON_PSA_SINGLE_CARD' && (
            <div 
              className="text-[#2966ff] text-sm font-normal leading-[21px] cursor-pointer hover:underline"
              onClick={handleCardTypeClick}
            >
              {t('sell.product_info.psa_card_indicator')}
            </div>
          )}
        </div>
      </div>
      
      <div className="px-6 pb-6">
        {type === 'PSA_SINGLE_CARD' && <PSACardInfo {...props} priceInputRef={priceInputRef} isPriceAdjustMode={isPriceAdjustMode} cardStats={cardStats} />}
        {type === 'NON_PSA_SINGLE_CARD' && <NonPSACardInfo {...props} priceInputRef={priceInputRef} isPriceAdjustMode={isPriceAdjustMode} cardStats={cardStats} />}
        {type === 'CARD_BOX' && <SealedBoxInfo {...props} priceInputRef={priceInputRef} isPriceAdjustMode={isPriceAdjustMode} cardStats={cardStats} />}
      </div>
    </div>
  );
};

export default CardSellInfoSection; 